import { Link } from 'react-router-dom';
import { useAuthStore, useIsAdmin, useAffiliatedSiteIds, isSiteManagerForSite } from '../lib/auth';
import { useQuery } from '@tanstack/react-query';
import { siteGetSites } from '../api';

export function DashboardPage() {
  const user = useAuthStore(state => state.user);
  const isAdmin = useIsAdmin();
  const siteIds = useAffiliatedSiteIds();
  
  const { data: sites, isLoading } = useQuery({
    queryKey: ['sites'],
    queryFn: () => siteGetSites(),
    enabled: isAdmin || siteIds.length > 0,
  });

  // Admins can see all sites, regular users only see their affiliated sites
  const userSites = isAdmin 
    ? sites 
    : sites?.filter(site => site.id && siteIds.includes(site.id));
  const currentTime = new Date().toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">
            {userSites?.[0]?.name || 'SmartField'}
          </h1>
          <p className="mt-2 text-gray-600">Welcome back, {user?.email}</p>
        </div>
        <div className="text-right text-sm text-gray-500">
          <div>{currentTime}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500">Account Type</h3>
          <p className="mt-2 text-3xl font-semibold text-smartfield">
            {isAdmin ? 'Admin' : 'User'}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500">Affiliated Sites</h3>
          <p className="mt-2 text-3xl font-semibold text-smartfield">
            {isLoading ? '...' : userSites?.length || 0}
          </p>
        </div>
      </div>

      {userSites && userSites.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Sites</h3>
          <div className="space-y-3">
            {userSites.map(site => (
              <div 
                key={site.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div>
                  <h4 className="font-medium text-gray-900">{site.name}</h4>
                  <p className="text-sm text-gray-500">{site.serialNumber}</p>
                </div>
                <div className="flex items-center gap-3">
                  {site.serialNumber && (
                    <Link
                      to={`/data/export?site=${encodeURIComponent(site.serialNumber)}`}
                      className="text-sm font-medium text-white bg-smartfield hover:bg-smartfield/90 px-3 py-1.5 rounded-md transition-colors"
                    >
                      Export data
                    </Link>
                  )}
                  {site.id && isSiteManagerForSite(user, site.id) && (
                    <Link
                      to={`/sites/${site.id}/geometry`}
                      className="text-sm font-medium text-smartfield border border-smartfield hover:bg-smartfield-50 px-3 py-1.5 rounded-md transition-colors"
                    >
                      Edit site
                    </Link>
                  )}
                  <span className="px-3 py-1 bg-smartfield-50 text-smartfield-700 rounded-full text-sm font-medium">
                    {site.siteType}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
